<template>
  <div v-if="currentUser" class="regulations-admin">
    <div class="page-header">
      <h1>Edycja dokumentów</h1>
      <button class="btn-primary" @click="editRegulations" :disabled="!isDocumentsLoaded">
        Zapisz zmiany
      </button>
    </div>

    <div class="regulations-layout">
      <div class="editor-section">
        <div class="section-card">
          <div class="section-card-header">
            <h2>Zawartość dokumentu</h2>
            <div class="toolbar">
              <label for="documentType">Wybierz dokument</label>
              <select
                  id="documentType"
                  class="document-select"
                  name="documentType"
                  v-model="documentType"
                  :disabled="!isDocumentsLoaded"
              >
                <option value="regulations">Regulamin</option>
                <option value="privacy">Polityka prywatności</option>
                <option value="cookies">Polityka plików cookies</option>
              </select>
              <button class="btn-green" @click="editRegulations" :disabled="!isDocumentsLoaded">
                Zatwierdź zmiany
              </button>
            </div>
          </div>

          <div v-if="isDocumentsLoaded" class="editor-wrapper">
            <CKEditor v-model="documents[documentType].content" />
          </div>

          <div v-else class="empty-state">
            <p>Ładowanie dokumentów...</p>
          </div>

          <div class="section-footer">
            <button class="btn-outline" @click="getDocuments">Odśwież dane</button>
            <button class="btn-green" @click="editRegulations" :disabled="!isDocumentsLoaded">
              Zapisz dokument
            </button>
          </div>
        </div>
      </div>

      <div class="details-section">
        <div class="section-card">
          <h2>Szczegóły</h2>

          <div v-if="isDocumentsLoaded" class="details-list">
            <div class="detail-item">
              <span class="detail-label">Aktualny dokument</span>
              <span class="detail-value">{{ currentDocumentTitle }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">Typ</span>
              <span class="detail-chip">{{ currentDocumentTypeLabel }}</span>
            </div>
            <div class="detail-item">
              <span class="detail-label">ID dokumentu</span>
              <span class="detail-value">{{ documents[documentType].id }}</span>
            </div>
          </div>

          <div v-else class="empty-state">
            <p>Brak danych dokumentu.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import CKEditor from '@/components/CKEditor.vue';
import authHeader from '@/services/auth-header';
import { useAuthStore } from '@/stores/auth';
import { useAppStore } from '@/stores/app';

export default {
  name: 'RegulationsEdit',
  components: { CKEditor },
  setup() {
    return {
      authStore: useAuthStore(),
      appStore: useAppStore(),
    };
  },
  data() {
    return {
      documentType: 'regulations',
      documents: {
        regulations: null,
        privacy: null,
        cookies: null,
      },
    };
  },
  props: ['isVisible'],
  computed: {
    currentUser() {
      return this.authStore.user;
    },
    isDocumentsLoaded() {
      return !!(this.documents.cookies && this.documents.regulations && this.documents.privacy);
    },
    currentDocumentTypeLabel() {
      if (this.documentType === 'regulations') return 'Regulamin';
      if (this.documentType === 'privacy') return 'Polityka prywatności';
      return 'Polityka plików cookies';
    },
    currentDocumentTitle() {
      if (!this.isDocumentsLoaded) return '';
      return this.documents[this.documentType].title;
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getDocuments();
  },
  methods: {
    getDocuments() {
      axios.get('/CmsAll').then(res => {
        this.documents.regulations = res.data[0];
        this.documents.privacy = res.data[1];
        this.documents.cookies = res.data[2];
      });
    },
    editRegulations() {
      if (!this.isDocumentsLoaded) return;

      const selectedDoc = this.documents[this.documentType];
      const ob = {
        id: selectedDoc.id,
        content: selectedDoc.content,
        title: selectedDoc.title,
      };

      axios.post('/Cms', ob, authHeader()).then(() => {
        this.appStore.setIsSuccess(`Pomyślnie zmieniono: ${selectedDoc.title}`);
      });
    },
  },
};
</script>

<style scoped>
.regulations-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 32px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.regulations-layout {
  display: flex;
  gap: 24px;
  align-items: flex-start;
}

.editor-section {
  flex: 1 1 0;
  min-width: 0;
}

.details-section {
  width: 320px;
  flex-shrink: 0;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0,0,0,0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0 0 16px 0;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 20px;
}

.toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.toolbar label {
  font-size: 1.4rem;
  font-weight: 600;
  color: #374151;
}

.document-select {
  min-width: 240px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 1.4rem;
  color: #1f2937;
  outline: none;
  background-color: #fff;
  transition: border-color 0.2s;
}

.document-select:focus {
  border-color: #10b981;
}

.editor-wrapper {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 12px;
  background: #fff;
}

.section-footer {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-bottom: 10px;
  border-bottom: 1px solid #e5e7eb;
}

.detail-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.detail-label {
  font-size: 1.3rem;
  color: #6b7280;
  font-weight: 600;
}

.detail-value {
  font-size: 1.5rem;
  color: #1f2937;
  word-break: break-word;
}

.detail-chip {
  width: fit-content;
  background-color: #d1fae5;
  color: #065f46;
  font-size: 1.2rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

/* Buttons */
.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 8px 16px;
  border-radius: 8px;
  border: none;
  font-size: 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.5rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

button:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.empty-state {
  text-align: center;
  padding: 24px;
  color: #9ca3af;
  font-size: 1.5rem;
}

@media (max-width: 980px) {
  .regulations-layout {
    flex-direction: column;
  }

  .details-section {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .regulations-admin {
    padding: 16px 12px;
  }

  .page-header {
    margin-bottom: 20px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .toolbar {
    flex-direction: column;
    align-items: stretch;
  }

  .document-select,
  .btn-green,
  .btn-outline,
  .btn-primary {
    width: 100%;
  }

  .section-footer {
    justify-content: stretch;
  }
}
</style>